import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";

/* One wizard shell for every Field Services flow. A step is:
   { key, title, hint?, render, blocked? }  — `blocked` is the SENTENCE saying why they
   can't move on yet, not a boolean, so the screen can always explain itself. */
export function Wizard({
  steps, onFinish, finishLabel = "Finish", finishIcon: FinishIcon = Check,
  testid = "wizard", busy = false,
}) {
  const live = (steps || []).filter(Boolean);
  const [i, setI] = useState(0);
  const [tried, setTried] = useState(false);
  const idx = Math.min(i, Math.max(0, live.length - 1));
  const step = live[idx] || { key: "empty", title: "", render: null };
  const last = idx === live.length - 1;
  const blocked = step.blocked || null;
  const go = (n) => { setTried(false); setI(Math.max(0, Math.min(live.length - 1, n))); };
  const advance = () => {
    if (blocked) return setTried(true);
    last ? onFinish?.() : go(idx + 1);
  };

  return (
    <div className="flex min-w-0 flex-col gap-5 sm:flex-row sm:gap-6" data-testid={testid}>
      {/* On a phone a horizontal strip of step chips is just something else to scroll past.
          The bar plus "step 3 of 6" plus the heading says the same thing in a third of the room. */}
      <ol className="hidden min-w-0 shrink-0 gap-1.5 sm:flex sm:w-48 sm:flex-col">
        {live.map((s, n) => {
          const done = n < idx;
          const here = n === idx;
          return (
            <li key={s.key} className="min-w-0">
              <button type="button" data-testid={`${testid}-step-${s.key}`}
                onClick={() => n <= idx && go(n)} disabled={n > idx}
                className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors ${here ? "bg-ink text-white" : done ? "text-ink/70 hover:bg-sand-100" : "text-ink/30"}`}>
                <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${here ? "bg-white/20 text-white" : done ? "bg-emerald-600 text-white" : "bg-sand-200 text-ink/45"}`}>
                  {done ? <Check className="h-3 w-3" /> : n + 1}
                </span>
                <span className="truncate text-[11px] font-bold uppercase tracking-wide">{s.title}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="min-w-0 flex-1">
        <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-sand-200 sm:hidden">
          <div className="h-full rounded-full bg-redline-500 transition-all duration-300"
            style={{ width: `${((idx + 1) / live.length) * 100}%` }} />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-ink/35">
          Step {idx + 1} of {live.length}
        </div>
        <h3 className="mt-0.5 font-heading text-lg font-extrabold tracking-tight text-ink">{step.title}</h3>
        {step.hint && <p className="mt-1 text-xs leading-relaxed text-ink/55">{step.hint}</p>}

        <AnimatePresence mode="wait">
          <motion.div key={step.key} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.16, ease: "easeOut" }}
            className="mt-4 space-y-3">
            {typeof step.render === "function" ? step.render() : step.render}
          </motion.div>
        </AnimatePresence>

        {blocked && tried && (
          <p data-testid={`${testid}-blocked`}
            className="mt-3 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs font-semibold leading-relaxed text-amber-900">
            {blocked}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2 border-t border-sand-200 pt-4">
          <button type="button" data-testid={`${testid}-back`} onClick={() => go(idx - 1)}
            disabled={idx === 0 || busy}
            className="inline-flex items-center gap-1 rounded-xl border border-sand-300 px-3.5 py-2 text-sm font-bold text-ink/60 transition-colors hover:border-ink/30 disabled:opacity-30">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <button type="button" data-testid={`${testid}-next`} onClick={advance} disabled={busy}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-40 ${blocked && tried ? "bg-ink/40" : last ? "bg-redline-500 hover:bg-redline-600" : "bg-ink hover:bg-ink/90"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
              : last ? <FinishIcon className="h-4 w-4" /> : null}
            {last ? finishLabel : "Continue"}
            {!last && <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export const WField = ({ label, hint, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/45">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs leading-relaxed text-ink/45">{hint}</span>}
  </label>
);

export const WNote = ({ children, tone = "sand", testid }) => {
  const cls = {
    sand: "bg-sand-100 text-ink/65", amber: "bg-amber-50 text-amber-900",
    emerald: "bg-emerald-50 text-emerald-800", indigo: "bg-indigo-50 text-indigo-900",
    red: "bg-red-50 text-red-900",
  }[tone];
  return <p data-testid={testid} className={`rounded-xl px-4 py-3 text-xs leading-relaxed ${cls}`}>{children}</p>;
};
